import { venueZone } from "../format.js";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  cleanPrefs,
  FLAVOUR_FAMILIES,
  FLAVOUR_GUEST_LABELS,
  lastSeenLabel,
  MAX_PREFERRED_FAMILIES,
  rankSpecsForTaste,
  SPEC_STYLES,
  type GuestPrefs,
  type GuestVisit,
  type VisitSource,
} from "@baros/core";
import { useApp } from "../context.js";
import type { ScreenProps } from "../screens.js";
import { guestsApi, type ListedGuest } from "./api.js";
import "./guests.css";

const SOURCE: Readonly<Record<VisitSource, string>> = {
  queue: "queued",
  manual: "logged by staff",
  square: "paid (Square)",
};

function day(iso: string): string {
  return new Date(iso).toLocaleDateString("en-AU", { timeZone: venueZone(), day: "numeric", month: "short", year: "numeric" });
}

/**
 * Guests (#127): the organisation's guest list, for managers. Honest about
 * what it knows — "last queued" when the queue is all that saw them, every
 * visit labelled with where it came from — because a list that implied
 * more would mislead the first time a Tuesday regular showed as a stranger.
 */
export function GuestsScreen({ params }: ScreenProps) {
  const app = useApp();
  const guestId = params["guestId"];
  const [q, setQ] = useState("");
  const [guests, setGuests] = useState<ListedGuest[] | null>(null);
  const [total, setTotal] = useState(0);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async (query: string) => {
    try {
      const res = await guestsApi.list(app.token, query);
      setGuests(res.guests);
      setTotal(res.total);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not read the guests");
    }
  }, [app.token]);

  useEffect(() => {
    const t = window.setTimeout(() => void load(q.trim()), 250);
    return () => window.clearTimeout(t);
  }, [q, load]);

  return (
    <div className="screen">
      <div className="row screen-head" style={{ padding: "18px 20px 12px", alignItems: "flex-end", gap: 14 }}>
        <div>
          <div style={{ fontSize: 22, fontWeight: 600, marginTop: 4 }}>{guests === null ? "Guests" : `${total} guest${total === 1 ? "" : "s"}`}</div>
        </div>
        <span className="spacer" />
        <input
          type="search"
          className="g-search"
          placeholder="Name, number or email"
          aria-label="Search guests"
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
      </div>
      {error && (
        <div style={{ padding: "0 20px 10px" }}>
          <div className="msg err">{error}</div>
        </div>
      )}

      <div className={`split g-split${guestId ? " has-detail" : ""}`}>
        <section className="pane g-list" style={{ padding: "0 20px 20px" }}>
          <div className="org-note" style={{ marginBottom: 10 }}>
            Guests join this list from the door queue. BarOS only sees a visit when somebody queues, a member of
            staff logs one, or (later) Square sees them pay — so a regular who always walks straight in may not be
            here. Records go two years after a guest was last seen.
          </div>
          {guests === null && !error && <div className="label">Reading the guests…</div>}
          {guests?.length === 0 && <div className="q-empty">{q ? "Nobody matches." : "No guests yet. They arrive with the door queue."}</div>}
          {guests?.map((g) => (
            <button
              key={g.guestId}
              className="g-row"
              aria-current={g.guestId === guestId}
              onClick={() => app.go({ kind: "guests", params: { guestId: g.guestId } })}
            >
              <span className="n">{g.name}</span>
              <span className="s">
                {g.phoneDisplay} · {lastSeenLabel(g.lastSeenSource)} {day(g.lastSeenAt)} · {g.visits} visit{g.visits === 1 ? "" : "s"}
                {g.marketing?.given ? " · promos OK" : ""}
              </span>
            </button>
          ))}
        </section>

        {guestId && (
          <section className="pane g-detail" style={{ padding: "0 20px 20px" }}>
            <GuestDetail
              key={guestId}
              guestId={guestId}
              onChanged={() => void load(q.trim())}
              onClose={() => app.go({ kind: "guests" })}
            />
          </section>
        )}
      </div>
    </div>
  );
}

function GuestDetail(props: { readonly guestId: string; readonly onChanged: () => void; readonly onClose: () => void }) {
  const app = useApp();
  const [guest, setGuest] = useState<ListedGuest | null>(null);
  const [visits, setVisits] = useState<GuestVisit[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    guestsApi
      .get(app.token, props.guestId)
      .then((res) => {
        setGuest(res.guest);
        setVisits(res.visits);
      })
      .catch((err: unknown) => setError(err instanceof Error ? err.message : "Could not read the guest"));
  }, [app.token, props.guestId]);

  const suggestions = useMemo(() => {
    if (!guest || guest.prefs.aromas.length === 0) return [];
    const pourable = app.library.specs.filter((s) => {
      const o = app.overrides.get(s.specId);
      return s.status === "available" && o && o.available !== false && o.price !== null;
    });
    const ingredients = new Map(app.library.ingredients.map((i) => [i.ingredientId, i]));
    return rankSpecsForTaste(pourable, ingredients, { families: guest.prefs.aromas, styles: guest.prefs.styles }).slice(0, 5);
  }, [guest, app.library, app.overrides]);

  if (error) return <div className="msg err">{error}</div>;
  if (!guest) return <div className="label">Reading…</div>;

  async function save(body: Parameters<typeof guestsApi.update>[2], done: string) {
    setBusy(true);
    setMsg(null);
    try {
      const res = await guestsApi.update(app.token, props.guestId, body);
      setGuest(res.guest);
      setMsg(done);
      props.onChanged();
    } catch (err) {
      setMsg(err instanceof Error ? err.message : "Could not save");
    } finally {
      setBusy(false);
    }
  }

  const venueName = (id: string) => app.me.venues.find((v) => v.venueId === id)?.name ?? id;

  return (
    <div className="g-card">
      <div className="row">
        <div style={{ fontSize: 20, fontWeight: 600 }}>{guest.name}</div>
        <span className="spacer" />
        <button className="btn sm g-close" onClick={props.onClose}>Close</button>
      </div>
      <div className="label" style={{ marginTop: 4 }}>
        {guest.phoneDisplay}
        {guest.email ? ` · ${guest.email}` : ""}
      </div>

      <div className="g-facts">
        <div>
          <div className="label">{lastSeenLabel(guest.lastSeenSource)}</div>
          <div>{day(guest.lastSeenAt)}</div>
        </div>
        <div>
          <div className="label">Visits seen</div>
          <div className="num">{guest.visits}</div>
        </div>
        <div>
          <div className="label">Promotions</div>
          <div>
            {guest.marketing?.given
              ? `Yes, ${guest.marketing.source === "guest-page" ? "ticked by them" : "told staff"} ${day(guest.marketing.at)}`
              : guest.marketing
                ? `No (since ${day(guest.marketing.at)})`
                : "Not asked"}
          </div>
        </div>
      </div>

      <PrefsEditor prefs={guest.prefs} busy={busy} onSave={(prefs) => void save({ prefs }, "Saved what they like.")} />

      {suggestions.length > 0 && (
        <div className="g-section">
          <div className="label">On tonight's list for them</div>
          <div className="g-suggest">
            {suggestions.map((m) => (
              <span key={m.spec.specId} className="g-chip">
                {m.spec.name}
              </span>
            ))}
          </div>
        </div>
      )}

      <div className="g-section">
        <div className="label">Visits</div>
        {visits.length === 0 && <div className="muted" style={{ fontSize: 13 }}>None seen yet — queued, but not yet seated.</div>}
        {visits.map((v) => (
          <div key={v.at} className="g-visit row">
            <span>{day(v.at)}</span>
            <span className="muted">{venueName(v.venueId)}</span>
            <span className="spacer" />
            <span className="label">{SOURCE[v.source]}</span>
          </div>
        ))}
      </div>

      {msg && <div className="msg ok" style={{ marginTop: 10 }}>{msg}</div>}

      <div className="g-section g-danger">
        {guest.marketing?.given && (
          <button className="btn sm" disabled={busy} onClick={() => void save({ marketing: false }, "Promotions consent withdrawn.")}>
            They don't want promotions
          </button>
        )}
        <button
          className="btn sm danger"
          disabled={busy}
          onClick={async () => {
            if (!window.confirm(`Delete everything held on ${guest.name}? This removes their record, number and visits and cannot be undone.`)) return;
            setBusy(true);
            try {
              await guestsApi.remove(app.token, props.guestId);
              props.onChanged();
              props.onClose();
            } catch (err) {
              setMsg(err instanceof Error ? err.message : "Could not delete");
              setBusy(false);
            }
          }}
        >
          Delete this guest
        </button>
      </div>
    </div>
  );
}

/** What a guest likes, in the wheel's twelve families and the four styles — never free text. */
export function PrefsEditor(props: { readonly prefs: GuestPrefs; readonly busy: boolean; readonly onSave: (p: GuestPrefs) => void }) {
  const [aromas, setAromas] = useState<string[]>([...cleanPrefs(props.prefs).aromas]);
  const [styles, setStyles] = useState<string[]>([...props.prefs.styles]);
  const changed = aromas.join() !== cleanPrefs(props.prefs).aromas.join() || styles.join() !== props.prefs.styles.join();
  return (
    <div className="g-section">
      <div className="label">Likes · up to {MAX_PREFERRED_FAMILIES}</div>
      <div className="g-aromas">
        {FLAVOUR_FAMILIES.map(({ key: family, colour }) => {
          const on = aromas.includes(family);
          return (
            <button
              key={family}
              type="button"
              className="g-aroma"
              aria-pressed={on}
              style={{ ["--c" as string]: colour }}
              disabled={!on && aromas.length >= MAX_PREFERRED_FAMILIES}
              onClick={() => setAromas(on ? aromas.filter((a) => a !== family) : [...aromas, family])}
            >
              {FLAVOUR_GUEST_LABELS[family]}
            </button>
          );
        })}
      </div>
      <div className="g-styles">
        {SPEC_STYLES.map((s) => (
          <button
            key={s}
            type="button"
            className="btn sm"
            aria-pressed={styles.includes(s)}
            onClick={() => setStyles(styles.includes(s) ? styles.filter((x) => x !== s) : [...styles, s])}
          >
            {s}
          </button>
        ))}
      </div>
      {changed && (
        <button className="btn sm primary" disabled={props.busy} onClick={() => props.onSave({ aromas, styles })}>
          Save
        </button>
      )}
    </div>
  );
}
